import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spinner, Alert } from 'react-bootstrap';
import { FaFileExcel } from 'react-icons/fa';
import api from '../../../services/api';
import { exportExcel } from '../../decisionnel/utils/exports';
import { Tableau, FicheLien, Statut, fmtMt, fmtNb, fmtDateFr } from './commun';

// Liste derrière un indicateur : GET /dashboard/v2/liste/:kind. Chaque ligne a un lien vers la fiche
// de l'entité (matériel, ouvrier, chantier, citerne) — ouverte dans un nouvel onglet.
// liste = { kind, titre, query } ; params = filtres du dashboard

const dt = (v) => (v ? fmtDateFr(String(v).slice(0, 10)) : '');
const dec = (v) => (v == null ? '—' : String(v).replace('.', ','));

// Colonnes « lien » (le libellé de la ligne ouvre la fiche)
const lien = (type, key, label, idKey) => ({
  key, label, render: (r, periode) => <FicheLien type={type} id={r[idKey]} periode={periode}>{r[key] || `#${r[idKey]}`}</FicheLien>,
  exportValue: r => r[key],
});
const L = {
  materiel: lien('materiel', 'matricule', 'Matricule', 'materielId'),
  ouvrier:  lien('ouvrier', 'nom', 'Ouvrier', 'ouvrierId'),
  chantier: lien('chantier', 'chantier', 'Chantier', 'chantierId'),
  citerne:  lien('citerne', 'nom', 'Citerne', 'citerneId'),
};
const C = {
  type: { key: 'type', label: 'Type' },
  societe: { key: 'societe', label: 'Société' },
  date: { key: 'date', label: 'Date', fmt: dt },
  montant: { key: 'montant', label: 'Montant (DH)', num: true, total: true, fmt: fmtMt },
  litres: { key: 'litres', label: 'Gasoil (L)', num: true, total: true, fmt: fmtNb },
  heures: { key: 'heures', label: 'Heures', num: true, total: true, fmt: fmtNb },
};

export const COLONNES = {
  ouvriers: [L.ouvrier, { key: 'qualification', label: 'Qualification' }, { key: 'chantiers', label: 'Chantier(s)' },
    { key: 'joursPointes', label: 'Jours pointés', num: true }, { key: 'jours', label: 'Jours', num: true, total: true, fmt: fmtNb },
    { key: 'heuresSup', label: 'Heures sup', num: true, total: true, fmt: fmtNb }, { key: 'masse', label: 'Masse (DH)', num: true, total: true, fmt: fmtMt }],
  materiels: [L.materiel, C.type, L.chantier, { key: 'joursPointes', label: 'Jours', num: true }, C.heures, C.litres,
    { key: 'litresHeure', label: 'L/h', num: true, fmt: dec }, { key: 'moyenneType', label: 'Moy. type (L/h)', num: true, fmt: dec },
    { key: 'anomalie', label: 'Conso anormale', fmt: v => (v ? '⚠️ Oui' : '') }],
  reparations: [C.date, L.materiel, C.type, L.chantier, { key: 'refBdc', label: 'Réf. BDC' }, { key: 'designation', label: 'Désignation' },
    { key: 'fournisseur', label: 'Fournisseur' }, { key: 'qte', label: 'Qté', num: true }, { key: 'prix', label: 'Prix', num: true, fmt: fmtMt }, C.montant],
  vidanges: [{ key: 'statut', label: 'Statut', render: r => <Statut s={r.statut} /> }, L.materiel, C.type, L.chantier, C.societe,
    { key: 'cycle', label: 'Depuis vidange', num: true, fmt: fmtNb }, { key: 'interval', label: 'Intervalle', num: true, fmt: fmtNb },
    { key: 'reste', label: 'Reste', num: true, fmt: fmtNb }, { key: 'mode', label: 'Unité' }],
  papiers: [{ key: 'statut', label: 'Statut', render: r => <Statut s={r.statut} /> }, L.materiel, C.type, { key: 'marque', label: 'Marque' },
    L.chantier, { key: 'papiers', label: 'Papiers concernés' }, { key: 'prochaine', label: 'Plus ancienne échéance', fmt: dt }],
  inactifs: [L.materiel, C.type, L.chantier, C.societe, { key: 'dernierPointage', label: 'Dernier pointage', fmt: dt },
    { key: 'joursInactif', label: 'Jours sans pointage', num: true }],
  chantiersSansPointage: [L.chantier, C.societe, { key: 'dernierPointage', label: 'Dernier pointage', fmt: dt },
    { key: 'ouvriers30', label: 'Ouvriers (30 j)', num: true }],
  citernes: [L.citerne, L.chantier, { key: 'stock', label: 'Stock (L)', num: true, total: true, fmt: fmtNb },
    { key: 'min', label: 'Min (L)', num: true, fmt: fmtNb }, { key: 'max', label: 'Max (L)', num: true, fmt: fmtNb },
    { key: 'remplissage', label: 'Remplissage', num: true, fmt: v => (v == null ? '—' : `${v} %`) },
    { key: 'consoJour', label: 'Conso / jour (L)', num: true, fmt: dec }, { key: 'autonomie', label: 'Autonomie (j)', num: true, fmt: dec }],
  ecarts: [C.date, L.materiel, C.type, L.chantier, { key: 'compteurPrec', label: 'Compteur avant', num: true },
    { key: 'compteurNv', label: 'Compteur après', num: true }, { key: 'heures', label: 'Heures déclarées', num: true }, { key: 'message', label: 'Écart' }],
  doublons: [C.date, L.ouvrier, { key: 'lignes', label: 'Lignes', num: true }, { key: 'jours', label: 'Jours', num: true }, { key: 'chantiers', label: 'Chantier(s)' }],
  mouvements: [C.date, { ...L.citerne, key: 'citerne' }, { key: 'type', label: 'Type' }, { key: 'litres', label: 'Litres', num: true, fmt: fmtNb },
    L.materiel, { key: 'commentaire', label: 'Commentaire' }],
};
COLONNES.anomalies = COLONNES.materiels;

const ListeModal = ({ liste, params, onClose }) => {
  const [data, setData]   = useState(null);
  const [error, setError] = useState('');
  const [recherche, setRecherche] = useState('');

  useEffect(() => {
    if (!liste) return;
    setData(null); setError(''); setRecherche('');
    api.get(`/dashboard/v2/liste/${liste.kind}`, { params: { ...params, ...(liste.query || {}) } })
      .then(r => setData(r.data))
      .catch(() => setError('Erreur lors du chargement de la liste'));
  }, [liste, params]);

  const periode = data?.periode;
  const colonnes = useMemo(() => (COLONNES[liste?.kind] || []).map(c => (c.render ? { ...c, render: r => c.render(r, periode) } : c)), [liste, periode]);
  const rows = useMemo(() => {
    const all = data?.rows || [];
    const q = recherche.trim().toLowerCase();
    return q ? all.filter(r => Object.values(r).some(v => String(v ?? '').toLowerCase().includes(q))) : all;
  }, [data, recherche]);

  const exporter = () => exportExcel(
    rows.map(r => Object.fromEntries((COLONNES[liste.kind] || []).map(c => [c.label,
      c.exportValue ? c.exportValue(r) : c.num ? Number(r[c.key] || 0) : (c.fmt ? c.fmt(r[c.key], r) : r[c.key] ?? '')]))),
    `dashboard-${liste.kind}-${periode?.end || ''}`
  );

  return (
    <Modal show={!!liste} onHide={onClose} size="xl" centered scrollable>
      <Modal.Header closeButton><Modal.Title style={{ fontSize: '1.05rem' }}>{liste?.titre}</Modal.Title></Modal.Header>
      <Modal.Body>
        {periode && <div className="small text-muted mb-2">Période : {dt(periode.start)} → {dt(periode.end)} · cliquez sur un nom pour ouvrir sa fiche</div>}
        <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <input className="form-control form-control-sm" style={{ maxWidth: 260 }} placeholder="Rechercher…"
            value={recherche} onChange={e => setRecherche(e.target.value)} />
          <span className="small"><strong>{rows.length}</strong> ligne{rows.length > 1 ? 's' : ''}</span>
          <button className="btn btn-sm btn-outline-success ms-auto" disabled={!rows.length} onClick={exporter}><FaFileExcel /> Excel</button>
        </div>
        {error && <Alert variant="warning" className="py-2">{error}</Alert>}
        {!data && !error && <div className="text-center py-4"><Spinner animation="border" size="sm" /> Chargement…</div>}
        {data && <Tableau columns={colonnes} rows={rows} total />}
      </Modal.Body>
    </Modal>
  );
};

export default ListeModal;
